/**
 * The map card under an answer, kept across a reload (QA 25.10, B13).
 *
 * The route / map-of-places / «Где вы?» card (`NavigationPayload`, one `scope` each) travels only in the custom
 * `navigation` SSE event: the checkpoint never holds it (INV-PH-66), so after an F5 the chat text and chips came
 * back from the thread's history and the card under them did not. The browser keeps a copy instead — the same
 * trade `activityStore` makes for the feeds (`ph.activity.<thread>`): best effort, never a thrown error.
 *
 * - ONE frame per thread, because that is all the chat ever draws: a newer frame replaces the older one live
 *   (`useNavigation.preview`), so an older turn's card is not on screen before the reload either. It is stored with
 *   `anchorId`, the last bubble of its turn — `turn` numbers do not survive an F5 (`chatStore.restore()` stamps
 *   every bubble `turn: 0`), message ids do.
 * - What is stored is the payload `readNavigationPayload` already bounded and cleaned (coordinates, titles, the
 *   2GIS links that passed `safe2gisLink`) — what the SSE delivered, nothing re-derived. On the way back it goes
 *   through the same reader (a shape this build no longer knows is dropped, not drawn half) but NOT through the
 *   position gate of `acceptNavigation`: that gate answers «is this route for the place the visitor is at NOW»,
 *   and a card restored from history is the answer as it was, not a fresh route.
 * - The 2GIS api key never lands here. A static-map URL carries it and lives only in a `map_image` attachment
 *   (AGENTS.md); no navigation payload has such a field, and a frame with anything key-shaped in it — a link with
 *   `key=`, a static-maps host — is not stored at all (a half-stripped frame would mislead), nor drawn if found.
 * - Capped: `MAX_NAV_THREADS` threads (the least recently written goes first), `MAX_NAV_BYTES` a frame (a long
 *   arrival line is thousands of points — over the cap the frame is simply not kept), `MAX_NAV_AGE_MS` old.
 * - The visitor's own position is never written (UI_CONTRACT §4.2: coordinates are not persisted): a frame whose
 *   start is their GPS, a point they picked or their address (`PERSONAL_SOURCES`) is not kept — review of !169. The
 *   museum's points, a plan's walk, a landmark named in the chat are.
 */

import { readNavigationPayload, type NavigationPayload } from './navigation';

const PREFIX = 'ph.nav.';
const VERSION = 1;
export const MAX_NAV_THREADS = 5;
export const MAX_NAV_BYTES = 200 * 1024;
const MAX_ANCHOR_CHARS = 200;
/** A day: a card to come back to after a reload, not a record of where the visitor went. */
export const MAX_NAV_AGE_MS = 24 * 60 * 60 * 1000;
/** Origins that ARE the visitor's position (`NavigationOrigin.source`), never stored. */
const PERSONAL_SOURCES = new Set(['gps', 'manual', 'address']);

function personal(payload: NavigationPayload): boolean {
  return [payload.origin, payload.request_origin].some((origin) => !!origin && PERSONAL_SOURCES.has(origin.source));
}

/** Anything that could carry the 2GIS key: a credential query parameter, or the static-maps host. */
const KEY_SHAPED = /[?&;](?:key|api[_-]?key|token|access_token)=|static\.maps\.2gis/i;

type Stored = { v: number; at: number; anchorId: string; payload: NavigationPayload };

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage ?? null : null;
  } catch {
    return null;
  }
}

/** The keys of this module in the storage, with the time each was written (a broken entry counts as oldest). */
function stored(store: Storage): { key: string; at: number }[] {
  const found: { key: string; at: number }[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (!key || !key.startsWith(PREFIX)) continue;
    let at = 0;
    try {
      const parsed: unknown = JSON.parse(store.getItem(key) ?? '');
      if (parsed && typeof parsed === 'object' && typeof (parsed as Stored).at === 'number') at = (parsed as Stored).at;
    } catch { /* an unreadable entry is the first to go */ }
    found.push({ key, at });
  }
  return found;
}

/** Keep the frame of `threadId`, replacing what the thread had; `false` when nothing was kept. */
export function rememberNavigation(threadId: string, anchorId: string, payload: NavigationPayload): boolean {
  forgetNavigation(threadId); // a frame that cannot be kept must not leave the older turn's card behind
  // «loading» is a spinner of a request that may never have finished — not an answer to come back to
  if (!threadId || !anchorId || anchorId.length > MAX_ANCHOR_CHARS || payload.status === 'loading' || personal(payload)) return false;
  try {
    const store = storage();
    if (!store) return false;
    const body = JSON.stringify({ v: VERSION, at: Date.now(), anchorId, payload } satisfies Stored);
    if (body.length > MAX_NAV_BYTES || KEY_SHAPED.test(body)) return false;
    store.setItem(PREFIX + threadId, body);
    // the one just written is never the one evicted, whatever the clock says
    const others = stored(store).filter((entry) => entry.key !== PREFIX + threadId).sort((a, b) => b.at - a.at);
    for (const old of others.slice(MAX_NAV_THREADS - 1)) store.removeItem(old.key);
    return true;
  } catch {
    return false; // storage absent, full or blocked: no card after a reload, an honest degradation
  }
}

/** The kept frame of `threadId` and the bubble it sits under, or `null`. Not a position check — see the header. */
export function recallNavigation(threadId: string): { anchorId: string; payload: NavigationPayload } | null {
  try {
    const raw = storage()?.getItem(PREFIX + threadId);
    if (!raw || KEY_SHAPED.test(raw)) return null;
    const entry = JSON.parse(raw) as Partial<Stored> | null;
    if (!entry || entry.v !== VERSION || typeof entry.anchorId !== 'string' || !entry.anchorId ||
        entry.anchorId.length > MAX_ANCHOR_CHARS || typeof entry.at !== 'number' || Date.now() - entry.at > MAX_NAV_AGE_MS) return null;
    const payload = readNavigationPayload(entry.payload);
    return payload && payload.status !== 'loading' && !personal(payload) ? { anchorId: entry.anchorId, payload } : null;
  } catch {
    return null;
  }
}

/** The thread's card is gone for good (a new chat, a place that changed, a newer card). */
export function forgetNavigation(threadId: string | null | undefined): void {
  if (!threadId) return;
  try {
    storage()?.removeItem(PREFIX + threadId);
  } catch { /* nothing to remove from */ }
}

/** «Забыть меня»: every thread's card, not only the open one. */
export function forgetAllNavigation(): void {
  try {
    const store = storage();
    if (!store) return;
    for (const { key } of stored(store)) store.removeItem(key);
  } catch { /* nothing to remove from */ }
}
